import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import SafeIcon from '../common/SafeIcon';
import Logo from './Logo';
import BoardSelector from './BoardSelector';

const { FiTrello, FiTrendingUp, FiCheckCircle, FiClock, FiAlertCircle, FiArrowLeft, FiActivity } = FiIcons;

const Dashboard = ({ boards, currentBoardId, onBoardSwitch }) => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('7d');

  const currentBoard = boards[boardId] || boards[currentBoardId] || { columns: {} };

  // Calculate metrics
  const calculateMetrics = () => {
    const totalTasks = Object.values(currentBoard.columns).reduce((sum, col) => sum + col.tasks.length, 0);
    const completedTasks = currentBoard.columns.done?.tasks.length || 0;
    const inProgressTasks = currentBoard.columns['in-progress']?.tasks.length || 0;
    const todoTasks = currentBoard.columns.todo?.tasks.length || 0;
    const backlogTasks = currentBoard.columns.backlog?.tasks.length || 0;

    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    // Calculate overdue tasks
    const now = new Date();
    const overdueTasks = Object.values(currentBoard.columns).reduce((sum, col) => {
      return sum + col.tasks.filter(task => {
        if (!task.dueDate) return false;
        return new Date(task.dueDate) < now;
      }).length;
    }, 0);

    // Calculate high priority tasks
    const highPriorityTasks = Object.values(currentBoard.columns).reduce((sum, col) => {
      return sum + col.tasks.filter(task => task.priority === 'high').length;
    }, 0);

    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      todoTasks,
      backlogTasks,
      completionRate,
      overdueTasks,
      highPriorityTasks
    };
  };

  // Prepare chart data
  const prepareChartData = () => {
    const metrics = calculateMetrics();
    
    // Column distribution data
    const columnData = [
      { name: 'Backlog', value: metrics.backlogTasks, color: '#6b7280' },
      { name: 'To Do', value: metrics.todoTasks, color: '#ef4444' },
      { name: 'In Progress', value: metrics.inProgressTasks, color: '#eab308' },
      { name: 'Done', value: metrics.completedTasks, color: '#22c55e' }
    ].filter(item => item.value > 0);

    // Priority distribution data
    const priorityData = [];
    Object.values(currentBoard.columns).forEach(column => {
      column.tasks.forEach(task => {
        if (task.priority) {
          const existing = priorityData.find(p => p.priority === task.priority);
          if (existing) {
            existing.count++;
          } else {
            priorityData.push({ priority: task.priority, count: 1 });
          }
        }
      });
    });

    return { columnData, priorityData };
  };

  const metrics = calculateMetrics();
  const { columnData, priorityData } = prepareChartData();

  const MetricCard = ({ title, value, icon, color, trend }) => (
    <motion.div
      className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color}`}>
          <SafeIcon icon={icon} className="w-6 h-6 text-white" />
        </div>
        {trend && (
          <span className={`text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
      <p className="text-sm text-gray-600 mt-1">{title}</p>
    </motion.div>
  );

  const handleBoardSwitch = (boardId) => {
    onBoardSwitch(boardId);
    navigate(`/dashboard/${boardId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <motion.header 
        className="bg-white shadow-sm border-b border-gray-200"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <motion.button
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
                </motion.button>
              </Link>
              <div className="flex items-center space-x-3">
                <Logo size="md" className="rounded-lg" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{currentBoard.name} Dashboard</h1>
                  <p className="text-sm text-gray-600">Board metrics and analytics</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
              <Link to="/">
                <motion.button
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiTrello} className="w-4 h-4" />
                  <span>View Board</span>
                </motion.button>
              </Link>
              <Link to="/global-dashboard">
                <motion.button
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiActivity} className="w-4 h-4" />
                  <span>Global Dashboard</span>
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Board Selector */}
      <BoardSelector
        boards={boards}
        currentBoardId={currentBoard.id || currentBoardId}
        onBoardSwitch={handleBoardSwitch}
      />

      <div className="container mx-auto px-4 py-8">
        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Total Tasks"
            value={metrics.totalTasks}
            icon={FiTrello}
            color="bg-blue-600"
          />
          <MetricCard
            title="Completed"
            value={metrics.completedTasks}
            icon={FiCheckCircle}
            color="bg-green-600"
          />
          <MetricCard
            title="In Progress"
            value={metrics.inProgressTasks}
            icon={FiClock}
            color="bg-yellow-600"
          />
          <MetricCard
            title="Completion Rate"
            value={`${metrics.completionRate}%`}
            icon={FiTrendingUp}
            color="bg-purple-600"
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Column Distribution Chart */}
          <motion.div
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Task Distribution</h3>
            {columnData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={columnData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {columnData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <p>No tasks to display</p>
              </div>
            )}
          </motion.div>

          {/* Priority Distribution Chart */}
          <motion.div
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Priority Distribution</h3>
            {priorityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={priorityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="priority" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <p>No priority data to display</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Additional Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Backlog Tasks</span>
                <span className="font-semibold text-gray-900">{metrics.backlogTasks}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">High Priority Tasks</span>
                <span className="font-semibold text-red-600">{metrics.highPriorityTasks}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Overdue Tasks</span>
                <span className="font-semibold text-orange-600">{metrics.overdueTasks}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Productivity Score</span>
                <span className="font-semibold text-green-600">{metrics.completionRate}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Tasks Pending</span>
                <span className="font-semibold text-yellow-600">{metrics.todoTasks + metrics.backlogTasks}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Active Tasks</span>
                <span className="font-semibold text-blue-600">{metrics.inProgressTasks}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;